import { useState, useMemo } from 'react';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { ChevronDown, ChevronRight, User, Package, Search, Users as UsersIcon } from 'lucide-react';

const SCOPE_STYLES = {
  ALL: 'bg-violet-100 text-violet-800 border-violet-200',
  TEAM: 'bg-sky-100 text-sky-800 border-sky-200',
  ASSIGNED: 'bg-amber-100 text-amber-800 border-amber-200',
  OWN_ONLY: 'bg-stone-100 text-stone-700 border-stone-200',
};

const SCOPE_LABEL = {
  ALL: 'All data',
  TEAM: 'Team + below',
  ASSIGNED: 'Assigned items',
  OWN_ONLY: 'Own records',
};

function flattenTree(nodes) {
  const byId = Object.fromEntries(nodes.map((n) => [n.id, n]));
  const childMap = {};
  const roots = [];
  for (const n of nodes) {
    const mgrId = n.reporting_to_user_id;
    if (mgrId && byId[mgrId]) (childMap[mgrId] = childMap[mgrId] || []).push(n);
    else roots.push(n);
  }
  const byName = (a, b) => a.name.localeCompare(b.name);
  Object.values(childMap).forEach((arr) => arr.sort(byName));
  roots.sort(byName);

  const flat = [];
  const walk = (node, depth, parent) => {
    const kids = childMap[node.id] || [];
    flat.push({ node, depth, parent, childCount: kids.length });
    kids.forEach((k) => walk(k, depth + 1, node));
  };
  roots.forEach((r) => walk(r, 0, null));
  return flat;
}

export function OrgChartView({ nodes = [] }) {
  const [collapsed, setCollapsed] = useState(new Set());
  const [query, setQuery] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const pool = useMemo(
    () => (showInactive ? nodes : nodes.filter((n) => n.is_active)),
    [nodes, showInactive]
  );
  const flat = useMemo(() => flattenTree(pool), [pool]);

  const matchedIds = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    const byId = Object.fromEntries(flat.map((f) => [f.node.id, f]));
    const keep = new Set();
    for (const f of flat) {
      const n = f.node;
      const hay = `${n.name} ${n.email} ${n.crm_role_name} ${n.profile_name}`.toLowerCase();
      if (hay.includes(q)) {
        keep.add(n.id);
        let p = f.parent;
        while (p) {
          keep.add(p.id);
          p = byId[p.id]?.parent;
        }
      }
    }
    return keep;
  }, [flat, query]);

  const visible = useMemo(() => {
    const ancestorAtDepth = [];
    const result = [];
    for (const item of flat) {
      ancestorAtDepth.length = item.depth;
      const hidden = !query && ancestorAtDepth.some((id) => collapsed.has(id));
      if (!hidden && (!matchedIds || matchedIds.has(item.node.id))) result.push(item);
      ancestorAtDepth[item.depth] = item.node.id;
    }
    return result;
  }, [flat, collapsed, matchedIds, query]);

  const toggle = (id) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  return (
    <div className="space-y-4" data-testid="org-chart-view">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email, Zoho role…"
            className="pl-8 rounded-sm"
            data-testid="org-chart-search"
          />
        </div>
        <button
          onClick={() => setShowInactive((v) => !v)}
          className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-4"
          data-testid="org-chart-toggle-inactive"
        >
          {showInactive ? 'Hide disabled users' : 'Show disabled users'}
        </button>
        <div className="ml-auto flex items-center gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <UsersIcon className="h-3.5 w-3.5" /> Sales / Purchase
          </span>
          <span className="flex items-center gap-1.5">
            <Package className="h-3.5 w-3.5 text-amber-600" /> Product
          </span>
          <span data-testid="org-chart-count">{visible.length} shown</span>
        </div>
      </div>

      {visible.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">No users to display</p>
      ) : (
        <div className="space-y-0.5">
          {visible.map(({ node, depth, childCount }) => {
            const isProduct = node.hierarchy_type === 'product';
            return (
              <div
                key={node.id}
                className={`flex items-center gap-2 py-1.5 pr-2 rounded-sm hover:bg-muted/50 ${
                  isProduct ? 'border-l-2 border-dashed border-amber-400' : 'border-l border-muted-foreground/25'
                }`}
                style={{ marginLeft: depth * 22, paddingLeft: depth > 0 ? 14 : 8, borderLeftWidth: depth > 0 || isProduct ? undefined : 0 }}
                data-testid={`org-node-${node.email}`}
              >
                {childCount > 0 ? (
                  <button
                    onClick={() => toggle(node.id)}
                    className="text-muted-foreground hover:text-foreground"
                    data-testid={`org-node-toggle-${node.email}`}
                  >
                    {collapsed.has(node.id) ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                  </button>
                ) : isProduct ? (
                  <Package className="h-3 w-3 text-amber-600 ml-0.5" />
                ) : (
                  <User className="h-3 w-3 text-muted-foreground/40 ml-0.5" />
                )}

                <span className={`text-sm font-medium shrink-0 ${!node.is_active ? 'text-muted-foreground line-through' : ''}`}>
                  {node.name}
                </span>
                {node.crm_role_name && (
                  <span className="text-xs text-muted-foreground shrink-0">{node.crm_role_name}</span>
                )}
                <span className="text-xs text-muted-foreground/70 truncate hidden lg:inline">{node.email}</span>

                <div className="ml-auto flex items-center gap-1.5 shrink-0">
                  {node.profile_name && (
                    <Badge variant="outline" className="text-[10px] rounded-sm font-normal">
                      {node.profile_name}
                    </Badge>
                  )}
                  {node.data_scope && (
                    <Badge
                      variant="outline"
                      className={`text-[10px] rounded-sm font-normal ${SCOPE_STYLES[node.data_scope] || ''}`}
                      data-testid={`org-node-scope-${node.email}`}
                    >
                      {SCOPE_LABEL[node.data_scope] || node.data_scope}
                    </Badge>
                  )}
                  {node.hierarchy_gap_reason && (
                    <Badge
                      variant="outline"
                      className="text-[10px] rounded-sm font-normal bg-amber-50 text-amber-800 border-amber-200"
                      title={node.hierarchy_gap_reason}
                      data-testid={`org-node-gap-${node.email}`}
                    >
                      Hierarchy gap
                    </Badge>
                  )}
                  {childCount > 0 && (
                    <span className="text-[10px] text-muted-foreground">{childCount} report{childCount > 1 ? 's' : ''}</span>
                  )}
                  {isProduct && (node.owned_skus > 0 || node.sub_assigned_skus > 0) && (
                    <span className="text-[10px] text-amber-700">
                      {node.owned_skus > 0 ? `${node.owned_skus} SKUs` : `${node.sub_assigned_skus} sub-SKUs`}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
